import React, { useEffect, useState } from 'react';
import { LayoutTemplate, CheckCircle2, FileText, Printer, Palette } from 'lucide-react';
import styles from './InvoiceTemplatePanel.module.css';

// 🚀 ADVANCED SYSTEM: 1 Perfect Layout, Multiple Colors, Thermal Options
const COLORS = [
    { id: 'themeClassic', name: 'Blue', hex: '#4f46e5' },
    { id: 'themeModern', name: 'Teal', hex: '#0f766e' },
    { id: 'themeMinimal', name: 'Slate', hex: '#334155' },
    { id: 'themeElegant', name: 'Gold', hex: '#b45309' },
    { id: 'themeRose', name: 'Rose', hex: '#e11d48' },
    { id: 'themePurple', name: 'Purple', hex: '#7c3aed' }
];

const InvoiceTemplatePanel = ({ selectedTemplate, onSelectTemplate }) => {
    const [mode, setMode] = useState('A4'); // 'A4' or 'THERMAL'
    const [a4Color, setA4Color] = useState('themeClassic');
    const [thermalSize, setThermalSize] = useState('paperThermal80');

    // Sync selected logic to parent dynamically
    useEffect(() => {
        if (mode === 'A4') {
            const colorObj = COLORS.find(c => c.id === a4Color);
            const generatedId = `a4-classic-${a4Color}`;
            
            if (selectedTemplate?.id !== generatedId) {
                onSelectTemplate({
                    id: generatedId,
                    name: 'Standard Professional',
                    format: 'A4 Document',
                    paperClass: 'paperA4',
                    themeClass: colorObj.id,
                    layoutClass: 'layoutClassic', // The 1 perfect layout
                    isThermal: false
                });
            }
        } else {
            const generatedId = `thermal-${thermalSize}`;
            
            if (selectedTemplate?.id !== generatedId) {
                const is80mm = thermalSize === 'paperThermal80';
                onSelectTemplate({
                    id: generatedId,
                    name: is80mm ? 'POS Receipt (80mm)' : 'Compact Receipt (58mm)',
                    format: 'Thermal Receipt',
                    paperClass: thermalSize,
                    themeClass: 'themeThermal',
                    isThermal: true
                });
            }
        }
    }, [mode, a4Color, thermalSize, onSelectTemplate, selectedTemplate?.id]);

    return (
        <aside className={styles.templatePanel} aria-label="Invoice Templates">
            <div className={styles.panelHeader}>
                <div className={styles.panelHeaderIcon}>
                    <LayoutTemplate size={16} />
                </div>
                <div>
                    <h2>Invoice Designer</h2>
                    <p>Customize your bill appearance</p>
                </div>
            </div>

            <div className={styles.templatePanelBody}>
                {/* 🚀 COMPACT MODE SWITCHER */}
                <div className={styles.modeSwitcher}>
                    <button 
                        type="button" 
                        className={`${styles.modeBtn} ${mode === 'A4' ? styles.modeActive : ''}`}
                        onClick={() => setMode('A4')}
                    >
                        <FileText size={13} /> A4 Invoice
                    </button>
                    <button 
                        type="button" 
                        className={`${styles.modeBtn} ${mode === 'THERMAL' ? styles.modeActive : ''}`}
                        onClick={() => setMode('THERMAL')}
                    >
                        <Printer size={13} /> Thermal POS
                    </button>
                </div>

                {/* 🚀 A4 MODE (1 Perfect Template + Colors) */}
                {mode === 'A4' && (
                    <div className={styles.fadeEnter}>
                        
                        <div className={styles.singleCardActive}>
                            <div className={styles.cardIcon}>
                                <FileText size={16} />
                            </div>
                            <div className={styles.cardInfo}>
                                <strong>Standard Professional</strong>
                                <span>Clean & optimized for sharing</span>
                            </div>
                            <CheckCircle2 size={16} className={styles.checkIcon} />
                        </div>

                        <div className={styles.configSection}>
                            <div className={styles.sectionHeading}>
                                <Palette size={12} />
                                <span>Theme Color</span>
                            </div>
                            <div className={styles.colorGrid}>
                                {COLORS.map((color) => (
                                    <button
                                        key={color.id}
                                        type="button"
                                        title={color.name}
                                        className={`${styles.colorSwatch} ${a4Color === color.id ? styles.colorActive : ''}`}
                                        style={{ backgroundColor: color.hex, outlineColor: color.hex }}
                                        onClick={() => setA4Color(color.id)}
                                    />
                                ))}
                            </div>
                        </div>

                    </div>
                )}

                {/* 🚀 THERMAL MODE (80mm & 58mm) */}
                {mode === 'THERMAL' && (
                    <div className={styles.fadeEnter}>
                        
                        <div className={styles.configSection}>
                            <div className={styles.sectionHeading}>
                                <Printer size={12} />
                                <span>Select Printer Size</span>
                            </div>
                            
                            <div className={styles.thermalList}>
                                <div 
                                    className={`${styles.thermalCard} ${thermalSize === 'paperThermal80' ? styles.thermalActive : ''}`}
                                    onClick={() => setThermalSize('paperThermal80')}
                                >
                                    <div className={styles.thermalInfo}>
                                        <strong>80mm Receipt</strong>
                                        <span>Standard 3-inch POS printers</span>
                                    </div>
                                    <div className={styles.radioCircle}>
                                        {thermalSize === 'paperThermal80' && <div className={styles.radioDot} />}
                                    </div>
                                </div>

                                <div 
                                    className={`${styles.thermalCard} ${thermalSize === 'paperThermal58' ? styles.thermalActive : ''}`}
                                    onClick={() => setThermalSize('paperThermal58')}
                                >
                                    <div className={styles.thermalInfo}>
                                        <strong>58mm Receipt</strong>
                                        <span>Compact 2-inch mini printers</span>
                                    </div>
                                    <div className={styles.radioCircle}>
                                        {thermalSize === 'paperThermal58' && <div className={styles.radioDot} />}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                )}
            </div>

            <div className={styles.templateHint}>
                <strong>Live Preview</strong>
                <span>Changes made here are instantly reflected on the invoice document on the left.</span>
            </div>
        </aside>
    );
};

export default InvoiceTemplatePanel;